import { feature } from "topojson-client";

import dataShootingsUSRaw from "./data/points/weapons_with_nationality_min.geo.json";
import dataManufacturersUSRaw from "./data/points/manufacturers_us.geo.json";
import dataManufacturersEuropeRaw from "./data/points/manufacturers_eu.geo.json";

import topology from "./data/geo/world_and_us_states_min.topo.json";
import europe from "./data/geo/central_europe.geo.json";

import weaponsSold from "./data/linecharts/weapons_sold_yearly.csv";
import manufacturerProduction from "./data/linecharts/production.csv";
import childrenDeaths from "./data/linecharts/children_deaths.csv";

import songsRaw from "./data/top_30_min.csv";

import { deepcopy, groupBy } from "./utils";

// ///////////////////////////////////////////////////////////
// SONGS
// ///////////////////////////////////////////////////////////

songsRaw.forEach((s) => {
	s.album_release_datestring = s.album_release_date;
	s.album_release_date = new Date(s.album_release_date);

	s.song_snipped = s.song_snipped === "NA" ? null : s.song_snipped;

	s.song_snipped_local = `${PATH}assets/audio/song_snippets/${s.id}.mp3`;
	s.cover_local = `${PATH}assets/images/album_covers/${s.id}.jpg`;

	s.song_spotify_embed = s.song_spotify.split("/track")[0] + "/embed/track" + s.song_spotify.split("/track")[1] + "?utm_source=generator&theme=white&view=coverart";

	const numeric = [
		"duration",
		"popularity",
		"danceability",
		"energy",
		"key",
		"loudness",
		"mode",
		"speechiness",
		"acousticness",
		"instrumentalness",
		"liveness",
		"valence",
		"tempo",
		"duration_ms",
		"time_signature",
		"year",
	];

	numeric.forEach((n) => {
		if (s[n])
			s[n] = +s[n];
	});
});

songsRaw.sort((a, b) => (a.popularity < b.popularity ? 1 : -1));

export const songs = songsRaw;

// ///////////////////////////////////////////////////////////
// DATA UTILS
// ///////////////////////////////////////////////////////////

const europeanWeapons = ["Austrian", "Belgium", "German", "Italian", "Romanian", "Spanish"];

export const isEuropeanWeapon = (props) => {
	let isEuropean = false;
	if (props.weapon_nationality) {
		props.weapon_nationality.forEach((n) => {
			if (europeanWeapons.includes(n)) isEuropean = true;
		});
	}
	return isEuropean;
};

const europeanManufacturers = ["SIG Sauer", "Glock", "Beretta", "Colt", "Walther", "Steyr", "CZ", "Heckler & Koch"];

export const isEuropeanManufacturer = (props) => europeanManufacturers.includes(props.name);

// ///////////////////////////////////////////////////////////
// DATA PROCESSING
// ///////////////////////////////////////////////////////////
const numeric = ["age_of_shooter", "fatalities", "injured", "total_victims", "year"];

dataShootingsUSRaw.features.forEach((f) => {
	// return if the data processing has already been done, i.e., the date is already a date object
	if (f.properties.datestring) return;

	f.properties.weapon_nationality = f.properties.weapon_nationality.split(", ");

	f.properties.datestring = f.properties.date;
	f.properties.date = new Date(f.properties.date);

	// // fix for one case where the number of injured is "fewer than 10" --> just take the fatalities
	// if (f.properties.total_victims === "TK") f.properties.total_victims = f.properties.fatalities;

	numeric.forEach((n) => {
		if (f.properties[n]) f.properties[n] = +f.properties[n];
	});
});

dataShootingsUSRaw.features.sort((a, b) => {
	if (a.properties.case === "Las Vegas Strip massacre") {
		return 1;
	}
	if (b.properties.case === "Las Vegas Strip massacre") {
		return -1;
	}
	return isEuropeanWeapon(a.properties) ? 1 : -1;
});

dataManufacturersUSRaw.features.forEach((f, i) => {
	f.id = `US${i}`;
});

dataManufacturersEuropeRaw.features.forEach((f, i) => {
	f.id = `EU${i}`;
});

dataManufacturersUSRaw.features.sort((a) => {
	return isEuropeanManufacturer(a.properties) ? 1 : -1;
});

const computeUSEuropeArcData = () => {
	const arcData = [];
	const flattenedData = dataManufacturersUSRaw.features.map(({ id, geometry, properties }) => ({
		id,
		geometry,
		...properties,
	}));
	const groupedByParent = groupBy(flattenedData, "name");

	Object.entries(groupedByParent).forEach(([parentKey, children]) => {
		if (!parentKey) return;
		const parentNode = dataManufacturersEuropeRaw.features.find((f) => f.properties.name === parentKey);
		children.forEach((childNode) => {
			if (parentNode?.geometry && childNode?.geometry) {
				arcData.push({
					id: `${parentNode.id}${childNode.id}`,
					source: childNode.geometry.coordinates,
					target: parentNode.geometry.coordinates,
				});
			}
		});
	});

	return arcData;
};

const addCountry = (data, country) => {
	data.features.forEach((f) => {
		f.properties.country = country;
	});
	return data;
};

export const dataShootingsUS = addCountry(dataShootingsUSRaw, "US");
export const dataManufacturersUS = addCountry(dataManufacturersUSRaw, "US");
export const dataManufacturersEurope = addCountry(dataManufacturersEuropeRaw, "EU");

export const dataManufacturersEuropeUS = deepcopy(dataManufacturersUS);
dataManufacturersEuropeUS.features = [...dataManufacturersUS.features, ...dataManufacturersEurope.features];

export const dataUSEuropeArcs = computeUSEuropeArcData();

// ///////////////////////////////////////////////////////////
// SET UP PROJECTIONS FOR EUROPE AND THE US
// ///////////////////////////////////////////////////////////

export const worldFeatures = feature(topology, topology.objects.countries_with_europe).features;
worldFeatures.sort((a, b) => (a.properties.name_en === "Europe" ? 1 : -1));
export const statesFeatures = feature(topology, topology.objects.states_with_us).features;
statesFeatures.sort((a, b) => (a.properties.name === "US" ? 1 : -1));

// create geojsons to fit the map to the US and Europe
export const geojsonFitUS = {
	type: "FeatureCollection",
	features: statesFeatures,
};
export const geojsonFitEurope = europe;
export const geojsonFitGermany = {
	type: "FeatureCollection",
	features: europe.features.filter((f) => f.properties.name_en === "Germany"),
};
export const geojsonFitEUManufacturers = {
	type: "FeatureCollection",
	features: europe.features.filter(
		(f) =>
			f.properties.name_en === "Germany" ||
			f.properties.name_en === "Austria" ||
			f.properties.name_en === "Switzerland" ||
			f.properties.name_en === "Croatia" ||
			f.properties.name_en === "Belgium" ||
			f.properties.name_en === "Slovakia"
	),
};

// rotation values for the US and Europe
export const rotateXUS = 97;
export const rotateYUS = -40;

export const rotateXEurope = -10;
export const rotateYEurope = -50;

export const childrenDeathsData = childrenDeaths.filter((d) => {
	return +d.year >= 2000;
});

export { weaponsSold, manufacturerProduction };
